<template>
<Drawer :title="$t('Edit evaluation levels')" initialWidth="xl" @requestUnmount="hide">

	<template #default="{ hide }">

		<div class="p-4">
			<div class="alert alert-warning text-sm items-start">
				<ExclamationTriangleIcon class="w-5 h-5 mr-2"></ExclamationTriangleIcon>
				{{ $t('#notification: beware changing evaluation levels') }}
			</div>
		</div>

		<form @submit.prevent="save" class="isolate">

			<div class="flex justify-end p-4">
				<button type="button" class="btn btn-circle" :class="[showNewLevelPlaceholders? 'btn-ghost bg-base-100':'btn-primary']" @click="showNewLevelPlaceholders = !showNewLevelPlaceholders">
					<PlusIcon class="w-5 h-5"></PlusIcon>
				</button>
			</div>

			<div class="pb-16 px-6 pt-6 flex flex-col gap-2">

				<div class="flex items-end">
					<div class="w-32 shrink-0 text-xs font-semibold text-base-content-light flex items-end">
						<div class="w-24 uppercase">{{ $t('Goal achieved') }}</div>
						<ArrowTurnRightUpIcon class="w-5 h-5"></ArrowTurnRightUpIcon>
					</div>
					<div class="flex flex-col gap-2 grow">
						<div class="w-full transition-all overflow-hidden" :class="[showNewLevelPlaceholders? 'h-6' : 'h-0']">
							<div class="flex gap-6 items-center pl-14 pr-12 relative">
								<button type="button" @click="addNewLevelAtPosition(groupedAndSortedLevelData.achieved[0]? groupedAndSortedLevelData.achieved[0].position + 1 :  1)" class="h-6 w-full rounded border border-dashed text-center relative flex justify-center items-center hover:bg-base-100">
									<PlusIcon class="w-3 h-3"></PlusIcon>
								</button>
							</div>
						</div>
						<template v-for="level in groupedAndSortedLevelData.achieved" :key="level.id">
							<div class="flex gap-4 items-center">
								<EvaluationNewLevelInput :level="level" :markInvalidInputs="markInvalidInputs" class="grow" />

								<Dropdown position="top-end">
									<template #trigger>
										<button type="button" class="btn btn-sm btn-ghost btn-circle">
											<TrashIcon class="w-4 h-4"></TrashIcon>
										</button>
									</template>
									<template #content="dialogActions">
										<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger" class="w-80"  @cancel="dialogActions.close" @confirm="removeLevel(level)">
											<span class="text-danger">{{ $t('Are you sure?') }}</span>
										</DropdownDialogConfirm>
									</template>
								</Dropdown>


							</div>
							<div class="w-full transition-all overflow-hidden" :class="[showNewLevelPlaceholders? 'h-6' : 'h-0']">
								<div class="flex gap-6 items-center pl-14 pr-12 relative">
									<button type="button" @click="addNewLevelAtPosition(level.position)" class="h-6 w-full rounded border border-dashed text-center relative flex justify-center items-center hover:bg-base-100">
										<PlusIcon class="w-3 h-3"></PlusIcon>
									</button>
								</div>
							</div>
						</template>
					</div>
				</div>
				<hr>
				<div class="flex items-start">
					<div class="w-32 shrink-0 text-xs font-semibold text-base-content-light flex items-start">
						<div class="w-24 uppercase">{{ $t('Goal not achieved yet') }}</div>
						<ArrowTurnRightDownIcon class="w-5 h-5"></ArrowTurnRightDownIcon>
					</div>
					<div class="flex flex-col gap-2 grow">
						<template v-for="level in groupedAndSortedLevelData.notAchieved" :key="level.id">
							<div class="w-full transition-all overflow-hidden" :class="[showNewLevelPlaceholders? 'h-6' : 'h-0']">
								<div class="flex gap-6 items-center pl-14 pr-12 relative">
									<button type="button" @click="addNewLevelAtPosition(level.position)" class="h-6 w-full rounded border border-dashed text-center relative flex justify-center items-center hover:bg-base-100">
										<PlusIcon class="w-3 h-3"></PlusIcon>
									</button>
								</div>
							</div>
							<div class="flex gap-4 items-center">
								<EvaluationNewLevelInput :level="level" :markInvalidInputs="markInvalidInputs" class="grow" />

								<Dropdown position="top-end">
									<template #trigger>
										<button type="button" class="btn btn-sm btn-ghost btn-circle">
											<TrashIcon class="w-4 h-4"></TrashIcon>
										</button>
									</template>
									<template #content="dialogActions">
										<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger" class="w-80"  @cancel="dialogActions.close" @confirm="removeLevel(level)">
											<span class="text-danger">{{ $t('Are you sure?') }}</span>
										</DropdownDialogConfirm>
									</template>
								</Dropdown>

							</div>
						</template>
						<div class="w-full transition-all overflow-hidden" :class="[showNewLevelPlaceholders? 'h-6' : 'h-0']">
							<div class="flex gap-6 items-center pl-14 pr-12 relative">
								<button type="button" @click="addNewLevelAtPosition(groupedAndSortedLevelData.notAchieved[0]? groupedAndSortedLevelData.notAchieved[groupedAndSortedLevelData.notAchieved.length - 1].position - 1 :  -1)" class="h-6 w-full rounded border border-dashed text-center relative flex justify-center items-center hover:bg-base-100">
									<PlusIcon class="w-3 h-3"></PlusIcon>
								</button>
							</div>
						</div>
					</div>
				</div>

			</div>

			<div class="drawer-action-bar justify-end">

				<div class="flex justify-end gap-1">
					<button type="button" class="btn btn-ghost" @click="hide" :class="[saving? 'hidden':'']">
						{{ $t('Cancel') }}
					</button>
					<button type="submit" class="btn btn-primary" :disabled="saving">
						<span v-show="saving" class="loading loading-spinner"></span>
						{{ $t('Save') }}
					</button>
				</div>

			</div>

		</form>

	</template>

</Drawer>
</template>

<script lang="ts">
import Drawer from '@/components/ui/Drawer.v2.vue';
import { PlusIcon, TrashIcon, ArrowTurnRightUpIcon, ArrowTurnRightDownIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import EvaluationNewLevelInput from '@/components/EvaluationNewLevelInput.vue';
import { mapStores } from 'pinia';
import { useEvaluationLevelsStore } from '@/stores/EvaluationLevels.store';
import { EvaluationLevel } from '@/models/EvaluationLevel.model';
import Dropdown from './ui/Dropdown.vue';
import DropdownDialogConfirm from './ui/DropdownDialogConfirm.vue';

export default {
	components: {
		Drawer,
		EvaluationNewLevelInput,
		PlusIcon, TrashIcon, ArrowTurnRightUpIcon, ArrowTurnRightDownIcon, ExclamationTriangleIcon,
		Dropdown,
		DropdownDialogConfirm,
	},

	data() {
		return {
			saving: false,
			showNewLevelPlaceholders: false,
			markInvalidInputs: false,	// e.g. we don't want to mark empty inputs as invalid when the user hasn't interacted with them yet

			levelData: [] as EvaluationLevel[],
		}
	},

	computed: {

		...mapStores(useEvaluationLevelsStore),

		groupedAndSortedLevelData() {
			const groupedAndSortedData = {
				'achieved': [] as EvaluationLevel[],
				'notAchieved': [] as EvaluationLevel[],
			}

			// sort by position
			this.levelData.sort((a, b) => b.position - a.position);

			// group by positive/negative position
			this.levelData.forEach(level => {
				if (level.position >= 0) {
					groupedAndSortedData.achieved.push(level);
				} else {
					groupedAndSortedData.notAchieved.push(level);
				}
			});

			return groupedAndSortedData;
		},
	},

	async mounted() {
		await this.evaluationLevelsStore.loadOnce();
		this.levelData = this.evaluationLevelsStore.items.map(level => {
			return level.clone();
		});
	},

	methods: {
		async save() {
			if(this.saving) {
				return;
			}
			this.saving = true;

			// check if the data is valid: each level has a name and hex color
			this.markInvalidInputs = true;
			let missingColorErrors = false;
			let missingNameErrors = false;

			this.levelData.forEach(level => {
				if (!level.name){
					missingNameErrors = true;
				}
				if(!level.color || !level.color.match(/^#[0-9a-f]{6}$/i)) {
					missingColorErrors = true;
				}
			});

			if(missingColorErrors || missingNameErrors) {
				this.saving = false;
				this.$notify({
					text: this.$t('Missing level color or name'),
					type: "error",
				});
				return;
			}

			await this.evaluationLevelsStore.updateEvaluationLevels(this.levelData);

			this.saving = false;
			this.hide();
		},

		hide() {
			this.$emit('requestClose');
		},

		addNewLevelAtPosition(position: number) {
			this.showNewLevelPlaceholders = false;

			// increase/decrease the position of all levels above/below the new level
			if(position >= 0) {
				this.levelData.forEach(level => {
					if (level.position >= position) {
						level.position++;
					}
				});
			} else {
				this.levelData.forEach(level => {
					if (level.position <= position) {
						level.position--;
					}
				});
			}

			const newLevel = new EvaluationLevel();
			newLevel.name = '';
			newLevel.color = '';
			newLevel.position = position;

			this.levelData.push(newLevel);
		},

		removeLevel(level: EvaluationLevel) {

			// As for confirmation

			const index = this.levelData.indexOf(level);
			if (index > -1) {
				this.levelData.splice(index, 1);
			}
			if (level.position >= 0) {
				this.levelData.forEach(l => {
					if (l.position > level.position) {
						l.position--;
					}
				});
			}
			else {
				this.levelData.forEach(l => {
					if (l.position < level.position) {
						l.position++;
					}
				});
			}
		},
	}
}
</script>
